import { telemetryIncrementAction } from 'core-events/actions';
import { JSX, useState } from 'react';
import { SearchFilterContentTypes, SearchOption } from 'search-gateway-client/components';
import { useStatsigExperimentContext } from 'statsig/components';
import { twMerge } from 'tailwind-merge';

import { Button } from '@videoblocks/storywind';

import { ConversationalSearchProvider, useConversationalSearch } from '../context/NavSearchContext';
import { CallToActionProps } from '../elements/CallToAction.builder';
import Image from '../elements/ProgressiveImage/ProgressiveImage';
import ProminentSearchLegacyContainer from '../elements/ProminentSearchLegacyContainer';
import { BuilderState } from '../types/shared';
import getAssetSource from '../utils/getAssetSource';
import getTrendingTagLinks from '../utils/getTrendingTagLinks';

/**
 * This component also exists in Stockblocks. The two should be kept in sync
 * until it goes away in Stockblocks.
 */

const imagesUrl = process.env.NEXT_PUBLIC_ASSET_PATH ?? '/images';

const eventTrackingForProminentSearch = () => {
    void telemetryIncrementAction('homepagesearch.trending_keyword_click');
};

// Button forwards unrecognized props onto its DOM element, so strip Builder's non-DOM config
// keys before spreading the config into <Button>.
const toButtonProps = (cta?: CallToActionProps): Partial<CallToActionProps> => {
    const rest: Partial<CallToActionProps> = { ...cta };
    delete rest.text;
    delete rest.showCallToAction;
    delete rest.mobileOnly;
    return rest;
};

export const defaultContentTypes: SearchOption[] = [
    {
        id: SearchFilterContentTypes.All_videos_content_type,
        label: 'Video',
        placeholderText: 'Search video library...',
    },
    {
        id: SearchFilterContentTypes.Templates,
        label: 'Video Templates',
        placeholderText: 'Search templates library...',
    },
    {
        id: SearchFilterContentTypes.All_audio_content_type,
        label: 'Audio',
        placeholderText: 'Search audio library...',
    },
    {
        id: SearchFilterContentTypes.All_images_content_type,
        label: 'Images',
        placeholderText: 'Search image library...',
    },
];

export interface Props {
    headingFirstLine?: string;
    headingSecondLine?: string;
    subheading?: string;
    videoSrc: string;
    posterSrc: string;
    trendingTags?: string;
    contentTypes?: SearchOption[];
    tall?: boolean;
    alignment?: 'left' | 'center';
    showCallToAction: boolean;
    callToAction?: CallToActionProps;
    showSecondaryCta: boolean;
    secondaryCta?: CallToActionProps;
    posterAlt?: string;
    builderState?: BuilderState;
}

function SearchHeroContent({
    headingFirstLine = 'Get Unlimited',
    headingSecondLine = 'Stock Media',
    subheading,
    trendingTags = 'Documentary,Love,Sport',
    videoSrc = '/home/video-homepage-hero-background.webm',
    posterSrc = '/home/video-homepage-hero-background.jpg',
    contentTypes = defaultContentTypes,
    tall,
    alignment,
    showCallToAction,
    callToAction,
    showSecondaryCta,
    secondaryCta,
    posterAlt = '',
}: Props): JSX.Element {
    const [contentType, setContentType] = useState(contentTypes[0]?.id);
    const { showConversationalSearchCta } = useStatsigExperimentContext();
    const { conversationalSearchEnabled } = useConversationalSearch();

    const hasAnyCtaButton = (showCallToAction && callToAction?.href) || (showSecondaryCta && secondaryCta?.href);

    // A `mobileOnly` primary CTA only shows below `md`; when it is the only button, hide the whole
    // row at `md`+ so its padding doesn't leave an empty gap once the button is hidden.
    const ctaRowMobileOnly = hasAnyCtaButton && !(showCallToAction && !callToAction?.mobileOnly) && !showSecondaryCta;

    const callToActionButtonProps = toButtonProps(callToAction);
    const secondaryCtaButtonProps = toButtonProps(secondaryCta);

    // Presentation is driven entirely by responsive CSS (breakpoint visibility classes), never by
    // Builder's device state: that state differs between server and client, so branching the render
    // on it makes the SSR and hydrated markup diverge. Everything below is always rendered — only
    // its per-breakpoint visibility changes (`md` ~ Builder's mobile boundary).
    return (
        <div className={twMerge('relative', tall ? 'min-h-100' : 'min-h-60', !trendingTags.length && 'mb-12')}>
            {/*Background*/}
            <div className="absolute top-0 left-0 w-full h-full overflow-hidden bg-gray-900">
                <div className="overflow-hidden w-full h-full">
                    <div className="transition-all h-full whitespace-nowrap">
                        <video
                            className="hero-video w-full h-full object-cover hidden md:block"
                            poster={getAssetSource(posterSrc)}
                            loop
                            autoPlay
                            muted
                        >
                            {/* `media` keeps the browser from fetching the video below `md`, where it is hidden. */}
                            <source src={getAssetSource(videoSrc)} type="video/mp4" media="(min-width: 672px)" />
                        </video>
                        <Image
                            className="absolute inset-0 object-cover w-full h-full md:hidden"
                            src={getAssetSource(posterSrc)}
                            alt={posterAlt}
                            shouldLazyLoad={true}
                            fill={true}
                        />
                    </div>
                </div>
            </div>
            {/*Header Container*/}
            <div
                className={twMerge(
                    'flex w-full relative z-10 m-auto',
                    alignment === 'left' ? 'justify-left' : 'justify-center',
                )}
            >
                <div
                    className={twMerge(
                        'gap-y-16 max-w-screen-2xl grid grid-cols-12 px-4 py-8 md:px-12 pt-8',
                        tall ? 'md:pt-36 lg:pt-36' : 'md:pt-20 lg:pt-20',
                        'lg:px-24',
                        hasAnyCtaButton && 'md:pt-12 md:pb-12',
                    )}
                >
                    {/*Header*/}
                    <div className="justify-center flex flex-col col-span-full">
                        {/*Header Text*/}
                        <div className="justify-center flex flex-col pb-5 md:pb-6 lg:pb-6">
                            <h1
                                className={twMerge(
                                    'font-polysans font-bold text-white mb-0',
                                    tall ? 'text-3xl ppro-sm:text-4xl md:text-7xl lg:text-7xl' : 'text-3xl',
                                    alignment === 'center' && 'text-center',
                                    // min-h-[2lh] reserves the natural height of two lines (using h1's own line-height,
                                    // so it tracks across breakpoints) so the search bar sits at the same Y position
                                    // whether the heading occupies one line or two (i.e. when AI CTA text is displayed).
                                    // only apply this styling if there is a second line of text... grid +
                                    // place-content-center vertically centers shorter copy within that reserved space.
                                    showConversationalSearchCta &&
                                        headingSecondLine &&
                                        'min-h-[2lh] grid place-content-center',
                                )}
                            >
                                {conversationalSearchEnabled ? (
                                    'Find the perfect video asset with AI Search'
                                ) : (
                                    <>
                                        {headingFirstLine}
                                        <br />
                                        {headingSecondLine}
                                    </>
                                )}
                            </h1>
                            {subheading && (
                                <span
                                    className={twMerge(
                                        'font-bold text-white font-polysans',
                                        tall ? 'mt-4 text-2xl' : 'mt-2',
                                        alignment === 'center' && 'text-center',
                                    )}
                                >
                                    {subheading}
                                </span>
                            )}
                        </div>
                        <div className="hidden md:block">
                            <div id="unifiedNav-search-app" className="prominent-search-bar">
                                <ProminentSearchLegacyContainer
                                    imagesUrl={imagesUrl}
                                    contentTypes={contentTypes}
                                    contentType={contentType}
                                    setContentType={setContentType}
                                    showDropdown={false}
                                    alignment={alignment}
                                    showContentToggles
                                    showConversationalSearchCta={showConversationalSearchCta}
                                />
                            </div>
                            {conversationalSearchEnabled ? (
                                // trending tag container provides padding / height needed for vertical alignment. replace when absent
                                <div className={twMerge(tall && 'pt-3 pb-32', hasAnyCtaButton && 'md:pb-6 lg:pb-6')} />
                            ) : (
                                !!trendingTags.length && (
                                    <div
                                        className={twMerge(
                                            'flex',
                                            tall && 'pt-6 pb-8 md:pb-24 lg:pb-24',
                                            hasAnyCtaButton && 'md:pb-6 lg:pb-6',
                                            alignment === 'center' && 'justify-center',
                                        )}
                                    >
                                        <p className="pr-1 text-white text-sm mobile-md:text-base ppro-sm:text-base md:text-base lg-text-base">
                                            Trending:
                                        </p>
                                        {trendingTags.split(',').map((tag, idx) => (
                                            <a
                                                className="homepage-trending text-white text-sm mobile-md:text-base ppro-sm:text-base md:text-base lg-text-base font-bold underline pr-1 underline-offset-5"
                                                href={getTrendingTagLinks(
                                                    tag.toLowerCase().replace(/\s/g, '-'),
                                                    contentType,
                                                )}
                                                onClick={eventTrackingForProminentSearch}
                                                key={tag}
                                            >
                                                {tag}
                                                {idx === trendingTags.split(',').length - 1 ? '' : ','}
                                            </a>
                                        ))}
                                    </div>
                                )
                            )}
                        </div>
                        {hasAnyCtaButton && (
                            <div
                                className={twMerge(
                                    'flex',
                                    tall && 'pt-4 pb-4',
                                    alignment === 'center' && 'justify-center',
                                    ctaRowMobileOnly && 'md:hidden',
                                )}
                            >
                                {showCallToAction && (
                                    <div className={twMerge('contents', callToAction?.mobileOnly && 'md:hidden')}>
                                        <Button className="no-underline" {...callToActionButtonProps}>
                                            {callToAction?.text}
                                        </Button>
                                    </div>
                                )}
                                {showSecondaryCta && (
                                    <Button className="no-underline ml-2" {...secondaryCtaButtonProps}>
                                        {secondaryCta?.text}
                                    </Button>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
}

function SearchHero(props: Props): JSX.Element {
    return (
        <ConversationalSearchProvider>
            <SearchHeroContent {...props} />
        </ConversationalSearchProvider>
    );
}

export default SearchHero;
